import React, { useState, useEffect, useRef } from "react";
import { DocumentArrowUpIcon, ArrowDownTrayIcon, XMarkIcon } from "@heroicons/react/24/solid";

export default function FileInput({
  value,
  onChange,
}) {
  const [fileName, setFileName] = useState(""); 
  const [fileUrl, setFileUrl] = useState(null); 
  const fileInputRef = useRef(null);

  // --- Lógica de Manejo de Archivo ---
  useEffect(() => {
    // 1. Cargar URL de la Base de Datos (string)
    if (value?.filePath && typeof value.filePath === 'string') {
      setFileUrl(value.filePath);
      // Extraer nombre limpio de la URL
      const cleanName = value.filePath.split("/").pop();
      setFileName(cleanName);
    // 2. Cargar objeto File local (File object)
    } else if (value?.filePath instanceof File) {
      setFileUrl(null); 
      setFileName(value.fileName || value.filePath.name);
    // 3. Estado vacío
    } else {
      setFileUrl(null);
      setFileName("");
    }
  }, [value]);

  const handleSelectFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Notificar al componente padre (ExecuteJob)
    onChange({
      fileType: file.type,
      filePath: file, // 💾 Se pasa el objeto File
      fileName: file.name,
    });
    // Resetear el input para permitir seleccionar el mismo archivo si es necesario
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemoveFile = () => {
    onChange(null); // Limpiar en el padre
    setFileName("");
    setFileUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  const downloadFile = () => {
    if (fileUrl) {
      // Descarga de URL externa (Base de Datos)
      window.open(fileUrl, "_blank");
    } else if (value?.filePath instanceof File) {
      // Descarga de archivo local (creando una URL temporal)
      const url = URL.createObjectURL(value.filePath);
      const a = document.createElement('a');
      a.href = url;
      a.download = value.fileName || 'archivo_descargado';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url); // ⚠️ Importante para limpiar la memoria
    }
  };

  const isFileLoaded = !!fileName;
  const isExternalUrl = !!fileUrl;
  // ------------------------------------

  return (
    <div className="flex flex-col gap-2 w-full">
      
      {/* Etiqueta */}
      <label className="block text-sm text-gray-600 font-medium mb-1">
        Archivo adjunto
      </label>

      {/* CONTENEDOR DE LA LÍNEA DE ACCIÓN (Horizontal y alineado a h-10) */}
      <div className="flex flex-wrap items-center gap-2 w-full">

        {/* 1. INPUT OCULTO */}
        <input
          type="file"
          className="hidden"
          ref={fileInputRef}
          onChange={handleSelectFile}
        />
        
        {/* 2. BOTÓN: Subir archivo */}
        <label 
          className="cursor-pointer bg-brickRed text-white px-3 py-2 rounded-md flex items-center gap-1.5 text-sm hover:bg-brickRedHover transition h-10"
          title="Seleccionar archivo"
        >
          <DocumentArrowUpIcon className="w-4 h-4" />
          Subir
        </label>

        {/* 3. BOTÓN: Descargar archivo */}
        <button
          disabled={!isFileLoaded}
          onClick={downloadFile}
          // Usamos h-10 para igualar la altura del botón Subir
          className={`px-3 py-2 rounded-md flex items-center gap-1.5 text-sm transition h-10
            ${isFileLoaded
              ? "bg-blue-600 text-white hover:bg-blue-700"
              : "bg-gray-200 text-gray-500 cursor-not-allowed"
            }`}
          title={isExternalUrl ? "Descargar archivo guardado" : "Descargar archivo seleccionado"}
        >
          <ArrowDownTrayIcon className="w-4 h-4" />
          Descargar
        </button>

        {/* 4. VISTA PREVIA Y ELIMINAR (El "badge" a la derecha, alineado a h-10) */}
        {isFileLoaded ? (
          <div className="flex items-center gap-1.5 px-3 h-10 border border-gray-300 rounded-md bg-gray-50 text-sm font-medium">
            <span className={`break-all ${isExternalUrl ? 'text-darkBrown' : 'text-gray-700'}`}>
              {fileName}
            </span>
            
            <button 
                onClick={handleRemoveFile}
                className="p-0.5 rounded-full text-gray-400 hover:text-red-600 transition ml-1"
                title="Eliminar archivo"
            >
                <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        ) : (
            <span className="text-sm text-gray-400 italic h-10 flex items-center">No hay archivo seleccionado.</span>
        )}

      </div>
    </div>
  );
}
